// =================== CONSTANT VALUES  ========================

export interface NotificationFrequency {
    name: string;
    value?: string;
}

export interface MessageType {
    message_type: string;
    action_type: "dropdown" | "switch";
    label: string;
    support_push: boolean;
    support_email: boolean;
    push_frequency: string;
    email_frequency: string;
    has_frequency_label: boolean;
    supported_frequencies: NotificationFrequency[];
}

export interface DefaultSetting {
    message_type: string;
    action_type: "dropdown" | "switch";
    push_frequency: string;
    email_frequency: string;
}

export const NOTIFICATION_FREQUENCIES: NotificationFrequency[] = [
    { name: "daily" },
    { name: "weekly" },
    { name: "never" },
];

// constants to map message type and label
export const SUPPORTED_MESSAGE_TYPES: Record<string, MessageType[]> = {
    classic: [
        {
            message_type: "user_referral",
            action_type: "switch",
            label: "When someone I told about PriceMe signs up:",
            support_push: true,
            support_email: true,
            push_frequency: "never",
            email_frequency: "daily",
            has_frequency_label: true,
            supported_frequencies: [
                { name: "On", value: "daily" },
                { name: "Off", value: "never" },
            ],
        },
        {
            message_type: "user_awards",
            action_type: "switch",
            label: "When I win a trophy:",
            support_push: true,
            support_email: false,
            push_frequency: "never",
            email_frequency: "daily",
            has_frequency_label: true,
            supported_frequencies: [
                { name: "On", value: "daily" },
                { name: "Off", value: "never" },
            ],
        },
    ],
    puzzle: [
        {
            message_type: "new_daily_puzzle",
            action_type: "dropdown",
            label: "When new properties are available:",
            support_push: true,
            support_email: true,
            push_frequency: "never",
            email_frequency: "daily",
            has_frequency_label: false,
            supported_frequencies: [{ name: "daily" }, { name: "weekly" }, { name: "never" }],
        },
        {
            message_type: "comment_reply",
            action_type: "switch",
            label: "When someone replies to my comment:",
            support_push: false,
            support_email: true,
            push_frequency: "never",
            email_frequency: "daily",
            has_frequency_label: true,
            supported_frequencies: [
                { name: "On", value: "daily" },
                { name: "Off", value: "never" },
            ],
        },
    ],
};

export const getDefaultSetting = (notify_daily = true, version = "classic"): DefaultSetting[] => {
    return [
        {
            message_type: "new_listings_available",
            action_type: "dropdown",
            push_frequency: "never",
            email_frequency: notify_daily && version === "classic" ? "daily" : "never",
        },
        {
            message_type: "new_user_tips",
            action_type: "dropdown",
            push_frequency: "never",
            email_frequency: version === "classic" ? "daily" : "never",
        },
        {
            message_type: "user_referral",
            action_type: "switch",
            push_frequency: "never",
            email_frequency: "daily",
        },
        {
            message_type: "user_awards",
            action_type: "switch",
            push_frequency: "never",
            email_frequency: version === "classic" ? "daily" : "never",
        },
        {
            message_type: "challenge_accept",
            action_type: "switch",
            push_frequency: "never",
            email_frequency: version === "classic" ? "daily" : "never",
        },
        {
            message_type: "new_daily_puzzle",
            action_type: "dropdown",
            push_frequency: "never",
            email_frequency: version === "puzzle" ? "daily" : "never",
        },
        {
            message_type: "comment_reply",
            action_type: "switch",
            push_frequency: "never",
            email_frequency: "daily",
        },
        // add more default notification settings
    ];
};

// This is the sorting order for the notifications based on the SUPPORTED_MESSAGE_TYPES constant
export const NOTIF_TYPE_SORT_ORDER = (gameVersion: string): Record<string, number> => {
    return SUPPORTED_MESSAGE_TYPES[gameVersion].reduce(
        (acc, curr, index) => {
            return {
                ...acc,
                [curr["message_type"]]: index + 1,
            };
        },
        {} as Record<string, number>,
    );
};

/**
 * Types of notifications available in the system
 * @type {Object}
 */
export const NOTIFICATION_TYPES = {
    STATUS_CHANGES: "social_status_change",
    COMMENTS_ON_MY_LISTINGS: "social_comment_on_my_listing",
    NEW_LISTINGS: "social_new_listings",
} as const;

/**
 * Default notification settings for new users
 * @type {Object}
 */
export const DEFAULT_NOTIFICATION_SETTINGS = {
    statusChanges: false,
    commentsOnMyListings: false,
    newListings: false,
} as const;

/**
 * Default form data structure for user settings
 * @type {Object}
 */
export const DEFAULT_FORM_DATA = {
    email_address: "",
    nickname: "",
    notifications: DEFAULT_NOTIFICATION_SETTINGS,
} as const;

// ============================================================
